import * as XLSX from "xlsx";
import moment from "@/utils/moment";
import { resolveAddress } from "../../../shared/api/tools";

type MutuaData = {
  headers: string[];
  data: any[][];
  startRow: number;
};

export interface ImportingService {
  raw: Record<string, any>;
  wgId: number;
  centralId: number;
  id: string;
  key: string;
  step: number;
  date: string;
  hour: string;
  advance: number;
  customer: {
    name: string;
    phone: string;
  };
  passenger: {
    name: string;
  };
  origin: {
    address: string;
    hint: string;
    notes: string;
    street?: number;
    portal?: number | null;
  };
  destination: {
    address: string;
    notes: string;
    street?: number;
    portal?: number;
  };
  requirements: {
    adapted: boolean;
  };
}

const ID = "ID";
const ID_TRAMO = "IDTramo";
const DATE = "Fecha";
const HOUR = "Hora Llegada";
const PASSENGER_NAME = "Paciente";
const CUSTOMER_PHONE = "Teléfonos";
const ORIGIN_ADDRESS = ["Dirección Origen", "Población Origen"];
const ORIGIN_NAME = "Nombre Origen";
const DESTINATION_ADDRESS = ["Dirección Destino", "Población Destino"];
const DESTINATION_NAME = "Nombre Destino";
const HINT = "Indicaciones al taxista";
const ADAPTED = "Transporte adaptado";

const getResolvedAddress = async (address: string, wgId: number, centralId: number) => {
  const resolved = await resolveAddress(address, wgId, centralId);
  return {
    provided: address,
    street: resolved?.street.id,
    portal: resolved?.portal,
  };
};

export const mapRowToService = async (
  row: Record<string, any>,
  wgId: number,
  centralId: number
): Promise<ImportingService> => {
  return {
    raw: row,
    id: row[ID],
    wgId,
    centralId,
    step: parseInt(row[ID_TRAMO]),
    key: row[ID] + "-" + row[ID_TRAMO],
    date: moment()(row[DATE], "DD/MM/YYYY").format("YYYY-MM-DD"),
    hour: row[HOUR],
    customer: {
      name: "",
      phone: row[CUSTOMER_PHONE].split(/,|;| /).map((i: string) => i.trim())[0],
    },
    passenger: {
      name: row[PASSENGER_NAME],
    },
    origin: {
      address: ORIGIN_ADDRESS.map((i) => row[i]).join(", "),
      hint: row[HINT],
      notes: row[ORIGIN_NAME],
      ...(await getResolvedAddress(ORIGIN_ADDRESS.map((i) => row[i]).join(", "), wgId, centralId)),
    },
    destination: {
      address: DESTINATION_ADDRESS.map((i) => row[i]).join(", "),
      notes: row[DESTINATION_NAME],
      ...(await getResolvedAddress(
        DESTINATION_ADDRESS.map((i) => row[i]).join(", "),
        wgId,
        centralId
      )),
    },
    requirements: {
      adapted: row[ADAPTED] !== "No",
    },
    advance: 0,
  };
};

export const processMutuaFile = async (
  file: File,
  wgId: number,
  centralId: number
): Promise<ImportingService[]> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = async (event) => {
      try {
        const data = new Uint8Array(event.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: "array" });

        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[][];

        let headerRowIndex = -1;
        let headers: string[] = [];

        for (let i = 0; i < jsonData.length; i++) {
          const row = jsonData[i];
          if (row && row.length > 6) {
            headerRowIndex = i;
            headers = row.map((cell) => String(cell || "")).filter((cell) => cell.trim() !== "");
            break;
          }
        }

        if (headerRowIndex === -1) {
          reject(
            new Error("No se encontró una fila con más de 6 columnas para usar como cabecera")
          );
          return;
        }

        const dataRows = jsonData
          .slice(headerRowIndex + 1)
          .filter(
            (row) =>
              row &&
              row.length > 0 &&
              row.some((cell) => cell !== null && cell !== undefined && cell !== "")
          );

        const rowsAsObjects = dataRows.map((row) => {
          const obj: Record<string, any> = {};
          headers.forEach((header, index) => {
            obj[header] = row[index];
          });
          return obj;
        });

        const services: ImportingService[] = [];
        for (const row of rowsAsObjects) {
          const service = await mapRowToService(row, wgId, centralId);
          services.push(service);
        }

        resolve(services);
      } catch (error) {
        reject(new Error(`Error al procesar el archivo Excel: ${error}`));
      }
    };

    reader.onerror = () => {
      reject(new Error("Error al leer el archivo"));
    };

    reader.readAsArrayBuffer(file);
  });
};

export const getColumnData = (mutuaData: MutuaData, columnIndex: number): any[] => {
  return mutuaData.data
    .map((row) => row[columnIndex])
    .filter((cell) => cell !== null && cell !== undefined);
};

export const getColumnDataByName = (mutuaData: MutuaData, columnName: string): any[] => {
  const columnIndex = mutuaData.headers.findIndex((header) =>
    header.toLowerCase().includes(columnName.toLowerCase())
  );

  if (columnIndex === -1) {
    return [];
  }

  return getColumnData(mutuaData, columnIndex);
};
